<template>
  <div class="time-duration__grid">
    <div class="time-duration__inputs-block">
      <ControlWrapper
        v-if="showDays"
        ref="daysControlWrapperRef"
        :class="`time-duration__input time-duration__input_${size}`"
        :addonAfter="tt('shared.shortDays')"
        :disabled="disabled"
        :isInvalid="isInvalid"
        :size="size"
      >
        <template #default="{ onFocus }">
          <NumberInput
            v-model:value="durationDays"
            name="days"
            :isOnlyInteger="isOnlyInteger"
            :hasBorder="false"
            :disabled="disabled"
            :isInvalid="isInvalid"
            :size="size"
            @focus="onFocus"
            @blur="handleDurationBlur"
          />
        </template>
      </ControlWrapper>

      <ControlWrapper
        v-if="showHours"
        ref="hoursControlWrapperRef"
        :class="`time-duration__input time-duration__input_${size}`"
        :addonAfter="tt('shared.shortHours')"
        :disabled="disabled"
        :isInvalid="isInvalid"
        :size="size"
      >
        <template #default="{ onFocus }">
          <NumberInput
            v-model:value="durationHours"
            name="hours"
            :isOnlyInteger="isOnlyInteger"
            :hasBorder="false"
            :size="size"
            :isInvalid="isInvalid"
            :disabled="disabled"
            @focus="onFocus"
            @blur="handleDurationBlur"
          />
        </template>
      </ControlWrapper>

      <ControlWrapper
        v-if="showMinutes"
        ref="minutesControlWrapperRef"
        :class="`time-duration__input time-duration__input_${size}`"
        :addonAfter="tt('shared.shortMinutes')"
        :disabled="disabled"
        :isInvalid="isInvalid"
        :size="size"
      >
        <template #default="{ onFocus }">
          <NumberInput
            v-model:value="durationMinutes"
            name="minutes"
            :isOnlyInteger="isOnlyInteger"
            :hasBorder="false"
            :size="size"
            :isInvalid="isInvalid"
            :disabled="disabled"
            @blur="handleDurationBlur"
            @focus="onFocus"
          />
        </template>
      </ControlWrapper>
    </div>
    <span
      v-if="helpText"
      class="time-duration__help-text"
    >
      {{ helpText }}
    </span>
  </div>
</template>

<script lang="ts">
import { Form } from 'ant-design-vue';
import {
  PropType,
  defineComponent,
  ref,
  watch,
} from 'vue';

import { NumberInput, ControlWrapper } from '@/ui';
import {
  HOURS_IN_DAY,
  SECONDS_IN_DAY,
  SECONDS_IN_HOUR,
  SECONDS_IN_MINUTE,
} from '@/constants';
import tt from '@/i18n/utils/translateText';
import { ESize } from '@/ui/types';

export default defineComponent({
  name: 'TimeDuration',
  components: {
    // eslint-disable-next-line vue/no-unused-components
    Form,
    NumberInput,
    ControlWrapper,
  },
  props: {
    duration: {
      type: [Number, null] as PropType<number | null>,
      default: 0,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    withDelimiter: {
      type: Boolean,
      default: true,
    },
    minDurationMinutes: {
      type: Number,
      default: 30,
    },
    helpText: {
      type: String,
      default: '',
    },
    showDays: {
      type: Boolean,
      default: true,
    },
    showHours: {
      type: Boolean,
      default: true,
    },
    showMinutes: {
      type: Boolean,
      default: true,
    },
    size: {
      type: String as PropType<ESize>,
      default: ESize.medium,
    },
    isInvalid: {
      type: Boolean,
      default: false,
    },
    isOnlyInteger: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['change'],
  setup(props, { emit }) {
    // Позволяет избежать warnings при использовании компонента внутри FromItem
    // The second way is to wrap it with a custom component and call useInjectFormItemContext in the custom component, It is equivalent to merging multiple form items into one.
    Form.useInjectFormItemContext();
    const daysControlWrapperRef = ref<HTMLDivElement | null>(null);
    const hoursControlWrapperRef = ref<HTMLDivElement | null>(null);
    const minutesControlWrapperRef = ref<HTMLDivElement | null>(null);

    const durationDays = ref<number>(0);
    const durationHours = ref<number>(0);
    const durationMinutes = ref<number>(0);

    const transformDurationToSeconds = () => {
      const durationInSeconds = ((durationDays.value * 24 * 60)
      + (durationHours.value * 60)
      + durationMinutes.value) * 60;

      emit('change', durationInSeconds);
    };

    const handleDurationBlur = () => {
      if (durationMinutes.value >= 60 && props.showHours) {
        durationHours.value += Math.floor(durationMinutes.value / 60);
        durationMinutes.value %= 60;
      }

      if (durationHours.value >= 24 && props.showDays) {
        durationDays.value += Math.floor(durationHours.value / 24);
        durationHours.value %= 24;
      }

      if (!durationDays.value
      && !durationHours.value
      && (!durationMinutes.value || durationMinutes.value < props.minDurationMinutes)) {
        durationMinutes.value = props.minDurationMinutes;
        durationHours.value = 0;
        durationDays.value = 0;
      }

      transformDurationToSeconds();
      daysControlWrapperRef.value?.blur();
      hoursControlWrapperRef.value?.blur();
      minutesControlWrapperRef.value?.blur();
    };

    const setDurationsFromProps = (duration: number | null) => {
      if (!duration) return;
      durationDays.value = Math.floor(duration / SECONDS_IN_DAY);
      durationHours.value = Math.floor((duration / SECONDS_IN_HOUR) % HOURS_IN_DAY);
      durationMinutes.value = (duration / SECONDS_IN_MINUTE) % SECONDS_IN_MINUTE;
      transformDurationToSeconds();
    };

    watch(() => props.duration, setDurationsFromProps, { immediate: true });

    return {
      durationDays,
      durationHours,
      durationMinutes,
      daysControlWrapperRef,
      hoursControlWrapperRef,
      minutesControlWrapperRef,

      handleDurationBlur,
      tt,
    };
  },
});
</script>

<style lang="scss" src="./styles.scss" />
